import { TiersAccountForm, TiersPersonnaliteJuridique, TiersStatus, isResultError, isResultSuccess, } from '../types';
export class TiersAccountService {
    tiersAccountAdapter;
    publicSettingsService;
    tiersService;
    constructor({ tiersAccountAdapter, publicSettingsService, tiersService }) {
        this.tiersAccountAdapter = tiersAccountAdapter;
        this.publicSettingsService = publicSettingsService;
        this.tiersService = tiersService;
    }
    /**
     * Return true, if the account is SIGNATAIRE regarding accounts list
     */
    isSignataire(account, accounts) {
        return accounts.some((acc) => {
            return acc.href === account.href && acc.form === TiersAccountForm.SIGNATAIRE;
        });
    }
    /**
     * Return true, if the account is ADMINISTRATOR
     */
    isAdministrator(account) {
        return account.form === TiersAccountForm.ADMINISTRATOR;
    }
    /**
     * Return true, if the account is franceConnect
     */
    isFranceConnect(account) {
        return account?.expand?.franceConnect === true;
    }
    /**
     * List linked user of the given tiers reference
     */
    async listTiersLinkedAccounts(tiersReference) {
        const result = await this.tiersAccountAdapter.listLinkedAccount(tiersReference);
        if (isResultError(result)) {
            throw result.error;
        }
        return result.response;
    }
    /**
     * Attach a user by it's email to a Tiers
     */
    async attachUserToTiers(tiers, email) {
        const publicSettings = await this.publicSettingsService.getPublicSettingsGestionDepotDemandes();
        const autoRattachementActif = publicSettings.autoRattachementByAdministrator.value;
        const shouldCheckKnownAccount = tiers.sansSIRET?.valeur || autoRattachementActif;
        let rattachementAuto = false;
        const accounts = await this.tiersAccountAdapter.listAccountsByEmail(email);
        const accountExists = isResultSuccess(accounts) && accounts.response.length > 0;
        if (shouldCheckKnownAccount && !accountExists) {
            rattachementAuto = true;
        }
        await (rattachementAuto
            ? this.tiersAccountAdapter.attachOrInviteUserToTiers(tiers.reference, email)
            : this.tiersAccountAdapter.attachUserToTiers(tiers.reference, email));
        const newAccounts = await this.listTiersLinkedAccounts(tiers.reference);
        return {
            accountExists,
            rattachementAuto,
            accounts: newAccounts,
        };
    }
    /**
     * Detach a linked account on Tiers
     */
    async detachAccount(account, tiers) {
        const result = await this.tiersAccountAdapter.detachAccount(tiers.reference, account.href);
        if (isResultError(result)) {
            throw result.error;
        }
        const accounts = await this.listTiersLinkedAccounts(tiers.reference);
        return {
            accounts,
        };
    }
    /**
     * Return true, if the currentUser is an ADMINISTRATOR on linkedAccounts
     */
    isCurrentAccountAdministratorOnTiers(currentUser, linkedAccounts = []) {
        const currentUserId = currentUser.self;
        return linkedAccounts.some((account) => account.href === currentUserId && this.isAdministrator(account));
    }
    /**
     * Return true, if the currentUser is allowed to add an account on Tiers
     */
    isAllowedToAddAccount(tiers, currentUser, linkedAccounts) {
        const hasStatusThatAllowsRattachement = tiers.status !== TiersStatus.TRANSMITTED;
        const isAdministrator = this.isCurrentAccountAdministratorOnTiers(currentUser, linkedAccounts);
        const isPersonaliteJuridiqueMorale = tiers.famille?.expand?.personnaliteJuridique === TiersPersonnaliteJuridique.MORALE;
        return hasStatusThatAllowsRattachement && isAdministrator && isPersonaliteJuridiqueMorale;
    }
    /**
     * Return true, if the currentUser is allowed to remove an account on Tiers
     */
    isAllowedToRemoveAccount(account, currentUser, linkedAccounts) {
        const isAdministrator = this.isCurrentAccountAdministratorOnTiers(currentUser, linkedAccounts);
        const accountToRemoveIsAnAdministrator = this.isAdministrator(account);
        return isAdministrator && !accountToRemoveIsAnAdministrator;
    }
    /**
     * Deduplicate linked account and sort them
     */
    filterAndSortAccounts(linkedAccounts) {
        return linkedAccounts
            .filter((account) => {
            return ![TiersAccountForm.SIGNATAIRE, TiersAccountForm.OWNER].includes(account.form);
        })
            .sort((account1, account2) => {
            const name1 = `${account1.form} ${account1.expand?.name?.familyName || ''} ${account1.expand?.name?.givenName || ''}`;
            const name2 = `${account2.form} ${account2.expand?.name?.familyName || ''} ${account2.expand?.name?.givenName || ''}`;
            return name1.localeCompare(name2);
        });
    }
    async listTiersAccountRestrictions() {
        const result = await this.tiersAccountAdapter.listTiersAccountRestrictions();
        if (isResultError(result)) {
            throw result.error;
        }
        return result.response;
    }
    async updateTiersAccountRestrictions(tiersHref, restrictions) {
        const result = await this.tiersAccountAdapter.updateTiersAccountRestrictions(tiersHref, restrictions);
        if (isResultError(result)) {
            throw result.error;
        }
        return result.response;
    }
    /**
     * Return true, if the currentUser is allowed to manage demande Apprenant
     */
    async isAllowedToManageDemandeApprenantRestrictions(tiers, currentUser, linkedAccounts) {
        const isAdministratorOnTiers = this.isCurrentAccountAdministratorOnTiers(currentUser, linkedAccounts);
        const isOrganismeFormation = this.tiersService.isOrganismeDeFormation(tiers);
        const isAllowedToManage = await this.tiersService.canCurrentTiersManageApprenants();
        return isAdministratorOnTiers && isOrganismeFormation && isAllowedToManage;
    }
}
//# sourceMappingURL=tiers-account-service.js.map